import React from "react";

import Layout from "~/components/layout";
import { LegalPageLayout } from "~/components/legal/legal-page-layout";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "~/components/ui/card";
import { H2, H3, H4, P, A, UL, LI } from "~/components/ui/typography";
import { cn } from "~/lib/utils";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { SUPPORT_EMAIL } from "~/lib/env.shared";

type TableOfContentsItem = {
    id: string;
    title: string;
    children?: TableOfContentsItem[];
};

export const PrivacyPage = () => {
    const tableOfContents = [
        { id: "i-who-or-what-is-evolve-labs", title: "Who is Evolve Labs?" },
        {
            id: "ii-key-terms--definitions-and-our-privacy-policy",
            title: "Key Terms & Definitions and Our Privacy Policy",
            children: [
                {
                    id: "when-does-our-privacy-policy-apply",
                    title: "When does our Privacy Policy apply?",
                },
                {
                    id: "when-does-our-privacy-policy-not-apply",
                    title: "When does our Privacy Policy not apply?",
                },
                {
                    id: "our-privacy-policy-and-terms-of-use",
                    title: "Our Privacy Policy and Terms of Use",
                },
            ],
        },
        {
            id: "iii-personal-information",
            title: "Personal Information",
            children: [
                {
                    id: "what-is-personal-information",
                    title: "What is Personal Information?",
                },
                {
                    id: "what-types-of-personal-information-do-we-collect",
                    title: "What types of Personal Information do we collect?",
                },
                {
                    id: "session-recordings-and-transcripts",
                    title: "Session Recordings and Transcripts",
                },
                { id: "health-information", title: "Health Information" },
                {
                    id: "how-do-we-collect-your-personal-information",
                    title: "How do we collect your Personal Information?",
                },
                {
                    id: "how-do-we-use-your-personal-information",
                    title: "How do we use your Personal Information?",
                },
                {
                    id: "how-do-we-share-your-personal-information",
                    title: "How do we share your Personal Information?",
                },
                {
                    id: "your-choices-about-how-we-share-your-personal-information",
                    title: "Your choices about how we share your Personal Information",
                },
                {
                    id: "how-do-i-access-and-correct-my-personal-information",
                    title: "How do I access and correct my Personal Information?",
                },
            ],
        },
        {
            id: "iv-who-may-use-the-services",
            title: "Who may use the Services?",
        },
        { id: "v-childrens-privacy", title: "Children's Privacy" },
        {
            id: "vi-does-evolve-labs-respond-to-do-not-track-signals",
            title: "Does Evolve Labs respond to Do Not Track signals?",
        },
        { id: "vii-data-security", title: "Data Security" },
        {
            id: "viii-californias-shine-the-light-law",
            title: "California's Shine the Light Law",
        },
        {
            id: "ix-changes-to-our-privacy-policy",
            title: "Changes to our Privacy Policy",
        },
        { id: "x-contact-us", title: "Contact Us" },
    ];

    const renderTableOfContents = (items: TableOfContentsItem[], level = 0) => (
        <UL
            className={cn(
                "space-y-1",
                level === 0 ? "text-sm" : "text-sm mt-1",
                level > 0 ? "pl-4" : "",
            )}
        >
            {items.map((item: TableOfContentsItem) => (
                <LI key={item.id} className="relative">
                    <A
                        href={`#${item.id}`}
                        className="block py-1 text-zinc-500 transition-colors duration-200 hover:text-rose-600"
                    >
                        {item.title}
                    </A>
                    {item.children &&
                        renderTableOfContents(item.children, level + 1)}
                </LI>
            ))}
        </UL>
    );

    return (
        <LegalPageLayout
            pageTitle="Privacy Policy"
            lastUpdated="April 2, 2025"
            tableOfContents={tableOfContents}
        >
            <section id="i-who-or-what-is-evolve-labs">
                <H2>I. Company & Product Information</H2>
                <P>
                    {PRODUCT_NAME} is developed by <strong>Evolve Labs</strong>,
                    which is part of Evolve. Evolve's mission is to expand
                    consciousness, reduce inner suffering, and help individuals
                    on their personal-transformation journeys. Evolve is an
                    umbrella organization with three symbiotic divisions: Evolve
                    Ventures, Evolve Foundation, and{" "}
                    <strong>Evolve Labs</strong>. Evolve Labs develops
                    technologies, products, and services in-house, including{" "}
                    {PRODUCT_NAME}. Evolve Labs is not a medical group or a
                    health care provider.
                </P>
                <P>
                    {PRODUCT_NAME} provides Internal Family Systems (IFS)
                    clients and practitioners with the ability to record,
                    transcribe, and analyze their therapy sessions. Our platform
                    allows clients and practitioners to either invite our bot to
                    their calendar events on platforms like Zoom or Google Meet,
                    or to upload recordings of their sessions directly.
                </P>
            </section>

            <section id="ii-key-terms--definitions-and-our-privacy-policy">
                <H2>II. Key Terms & Definitions</H2>
                <P>
                    It is helpful to start by explaining some of our key terms
                    and definitions used in this Privacy Policy.
                </P>

                <div className="space-y-2">
                    <div>
                        <H4 className="font-medium">"Personal Information"</H4>
                        <P className="text-gray-800">
                            Any information relating to an identified or
                            identifiable individual and any information listed
                            here.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">our "Platform"</H4>
                        <P className="text-md text-gray-800">
                            Our Website and/or Software.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">"Privacy Policy"</H4>
                        <P className="text-md text-gray-800">
                            This privacy policy.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">"Products"</H4>
                        <P className="text-md text-gray-800">
                            Any products available for purchase on our Platform.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">our "Services"</H4>
                        <P className="text-md text-gray-800">
                            Any services provided through our Platform.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">our "Software"</H4>
                        <P className="text-md text-gray-800">
                            Our software, including the {PRODUCT_NAME} web
                            application.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">our "Terms of Service"</H4>
                        <P className="text-md text-gray-800">
                            Our{" "}
                            <A
                                href="/terms"
                                className="text-rose-600 hover:underline"
                            >
                                terms of service
                            </A>{" "}
                            found here.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">our "Website(s)"</H4>
                        <P className="text-md text-gray-800">
                            Our websites, including: www.evolve.org,
                            www.partscompanion.org, app.partscompanion.org, and
                            app.ifscompanion.org
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">
                            "Evolve Labs," "we," "us," or "our"
                        </H4>
                        <P className="text-md text-gray-800">
                            Evolve Labs, Inc.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">"Client-Users"</H4>
                        <P className="text-md text-gray-800">
                            Individuals who use our Platform to analyze their
                            therapy sessions.
                        </P>
                    </div>

                    <div>
                        <H4 className="font-medium">"Practitioner-Users"</H4>
                        <P className="text-md text-gray-800">
                            Therapists, counselors, or other professionals who
                            use our Platform to analyze sessions with their
                            clients.
                        </P>
                    </div>
                </div>

                <section id="when-does-our-privacy-policy-apply">
                    <H3>When does our Privacy Policy apply?</H3>
                    <P className="text-md mb-4 text-gray-800">
                        This Privacy Policy describes the types of information
                        we may collect from you when:
                    </P>
                    <UL className="mb-4 list-disc space-y-1 pl-5">
                        <LI className="text-md text-gray-800">
                            You visit or use our Platform, including our Website
                            and/or Software;
                        </LI>
                        <LI className="text-md text-gray-800">
                            We communicate in e-mail, text message, and other
                            electronic messages between you and us;
                        </LI>
                        <LI className="text-md text-gray-800">
                            You upload, record, or process therapy sessions
                            through our Platform; and
                        </LI>
                        <LI className="text-md text-gray-800">
                            You interact with our bot or AI-powered features.
                        </LI>
                    </UL>
                </section>

                <section id="when-does-our-privacy-policy-not-apply">
                    <H3>When does our Privacy Policy not apply?</H3>
                    <P>
                        This Privacy Policy does not apply to information
                        collected by any other website operated either by us or
                        by a third party, unless the website is listed above or
                        links to this Privacy Policy. It also does not apply to
                        any website that we may provide a link to or that is
                        accessible from our Platform. Our Platform is powered
                        and operated by Evolve Labs, Inc.
                    </P>
                </section>

                <section id="our-privacy-policy-and-terms-of-use">
                    <H3>Our Privacy Policy and Terms of Use.</H3>
                    <P>
                        This Privacy Policy is incorporated into our{" "}
                        <A
                            href="/terms"
                            className="text-rose-600 hover:underline"
                        >
                            Terms of Use
                        </A>
                        , which also applies when you use our Platform.
                    </P>
                </section>
            </section>

            <section id="iii-personal-information">
                <H2>III. Personal Information</H2>

                <section id="what-is-personal-information">
                    <H3>What is Personal Information?</H3>
                    <P>
                        Personal information is information from and about you
                        that may directly or indirectly identify you. We treat
                        any information that may identify you as personal
                        information. For example, your name and e-mail address
                        are personal information.
                    </P>
                </section>

                <section id="what-types-of-personal-information-do-we-collect">
                    <H3>Which types of Personal Information do we collect?</H3>
                    <P>
                        We may collect and use the following personal
                        information (hereinafter referred to as "Personal
                        Information"):
                    </P>

                    <P className="text-md mb-2 text-gray-800">
                        Specific Types of Personal Information Collected:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>Personal Identifiers</LI>
                    </UL>

                    <P className="text-md mb-2 font-medium">
                        Categories of Personal Information
                    </P>
                    <P>
                        Information that identifies, relates to, describes, or
                        is capable of being associated with a particular
                        individual name, username or online identifier, physical
                        characteristics or description, shipping address,
                        telephone number, credit card number, debit card number,
                        or any other financial information, health or medical
                        information.
                    </P>

                    <P className="text-md mb-2">
                        Characteristics of protected classifications under
                        federal law:
                    </P>
                    <ol className="text-md mb-4 list-decimal space-y-1 pl-5 text-gray-800">
                        <LI>
                            Race, Color, Medical Conditions, Age, Gender/Sex,
                            National origin, or Disability
                        </LI>
                        <LI>
                            Internet or other electronic network activity
                            information
                        </LI>
                        <LI>
                            IP address, device mode, device ID, OS version,
                            device language, operating system, browser type,
                            browsing history, search history, and information
                            regarding a consumer's interaction with an Internet
                            Web site, application, or advertisement.
                        </LI>
                        <LI>Geolocation data</LI>
                        <LI>
                            Physical location or movements, local time, and
                            local time zone.
                        </LI>
                    </ol>
                </section>

                <section id="session-recordings-and-transcripts">
                    <H3>Session Recordings and Transcripts</H3>
                    <P>
                        We collect and process audio recordings and transcripts
                        of therapy sessions that you provide to us when using
                        our Platform. This includes sessions recorded through
                        our recorder when invited to a calendar event, or
                        sessions directly uploaded to our Platform. These
                        recordings and transcripts may contain sensitive
                        personal and health information.
                    </P>

                    <P className="text-md mb-2">
                        How we handle session recordings and transcripts:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            All recordings and transcripts are encrypted both at
                            rest and in transit.
                        </LI>
                        <LI>
                            Recordings and transcripts are stored indefinitely
                            unless you choose to delete them or request deletion
                            by contacting us via email at
                            {SUPPORT_EMAIL}.
                        </LI>
                        <LI>
                            Recordings and transcripts are analyzed using our AI
                            processing pipeline to generate reports and
                            insights. All partners handling such information are
                            Business Associates, subject to the same PHI
                            handling standards under HIPAA.
                        </LI>
                        <LI>
                            Access to recordings and transcripts within our
                            company is restricted and monitored.
                        </LI>
                        <LI>
                            Your recordings and transcripts may be accessed
                            where necessary to provide Services or when you
                            explicitly authorize the use of session data for
                            research and development purposes
                        </LI>
                    </UL>
                </section>

                <section id="health-information">
                    <H3>Health Information</H3>
                    <P>
                        When combined with health information, some Personal
                        Information we collect may constitute protected health
                        information under the Health Insurance Portability and
                        Accountability Act of 1996 ("HIPAA"). Evolve Labs is
                        operates under HIPAA regulations and strictly enforces
                        HIPAA requirements with partnerships through Business
                        Associate Agreements (BAAs).
                    </P>
                    <P>
                        For Practitioner-Users who are covered entities under
                        HIPAA, we offer a Business Associate Agreement that can
                        be accessed at{" "}
                        <A
                            href="https://app.ifscompanion.org/onboarding/practitioner/baa"
                            className="text-rose-600 hover:underline"
                        >
                            https://app.ifscompanion.org/onboarding/practitioner/baa
                        </A>
                        .
                    </P>
                    <P>
                        We collect, use, and disclose protected health
                        information for the purposes of providing the Services
                        and we only collect the minimum amount necessary to
                        fully perform and provide the Services on our Platform.
                        Protected health information will not be used for any
                        other purpose without your consent.
                    </P>
                </section>

                <section id="how-do-we-collect-your-personal-information">
                    <H3>How do we collect your Personal Information?</H3>
                    <P>
                        We collect most of this Personal Information directly
                        from you. For example, when we speak to you by phone,
                        text message, and e-mail. Additionally, we will collect
                        information from you when you visit our Website, submit
                        forms, contact us, use our Software, or purchase our
                        Services.
                    </P>

                    <P className="text-md mb-2 text-gray-800">
                        We may also collect Personal Information:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            Through recordings of therapy sessions when you
                            invite our recorder or through direct uploads.
                        </LI>
                        <LI>
                            When you make payments through the Platform. We do
                            not collect or store financial account information,
                            though we may receive transaction identifiers and
                            summary information that does not include credit
                            card or bank account numbers.
                        </LI>
                        <LI>
                            When You Contact Us. When you contact Evolve Labs
                            directly, such as when you contact our Customer
                            Support team, we will receive the contents of your
                            message or any attachments you may send to us, as
                            well as any additional information you choose to
                            provide.
                        </LI>
                        <LI>
                            When you use our Platform anonymously (without
                            creating an account), we collect identifiable
                            information, such as IP address, through the use of
                            cookies and for the purpose of maintaining your
                            session for up to 30 days.
                        </LI>
                    </UL>

                    <P className="text-md mb-2 text-gray-800">
                        We will also collect information automatically as you
                        navigate through our Platform. We use the following
                        technologies and third-party services to automatically
                        collect data:
                    </P>

                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            <P className="font-medium">Cookies.</P>
                            <P className="text-md text-gray-800">
                                We and our service providers may use cookies,
                                web beacons, and other technologies to receive
                                and store certain types of information whenever
                                you interact with our Platform or Services
                                through your computer or mobile device. A
                                "cookie" is a small file or piece of data sent
                                from a website and stored on the hard drive of
                                your computer or mobile device. Some of the
                                cookies we use are "session" cookies, meaning
                                that they are automatically deleted from your
                                hard drive after you close your browser at the
                                end of your session. Session cookies are used to
                                optimize performance of the Website and to limit
                                the amount of redundant data that is downloaded
                                during a single session. We also may use
                                "persistent" cookies, which remain on your
                                computer or device unless deleted by you (or by
                                your browser settings). We may use persistent
                                cookies for various purposes, such as
                                statistical analysis of performance to ensure
                                the ongoing quality of our Platform and/or the
                                Services. We and third parties may use session
                                and persistent cookies for analytics and
                                advertising purposes, as described herein. On
                                your computer, you may refuse to accept browser
                                cookies by activating the appropriate setting on
                                your browser, and you may have similar
                                capabilities on your mobile device in the
                                preferences for your operating system or
                                browser. However, if you select this setting you
                                may be unable to access or use certain parts of
                                our Platform or the Services. Unless you have
                                adjusted your browser or operating system
                                setting so that it will refuse cookies, our
                                system will issue cookies when you direct your
                                browser to our Platform.
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">
                                Facebook Pixel and Instagram.
                            </P>
                            <P className="text-md text-gray-800">
                                We use Facebook Pixel and Instagram, a web
                                analytics and advertising service provided by
                                Facebook Inc. ("Facebook") on our Platform. With
                                its help, we and our customers can keep track of
                                what users do after they see or click on a
                                Facebook or Instagram advertisement, keep track
                                of users who access our Platform or
                                advertisements from different devices, and
                                better provide advertisements to our target
                                audiences. The data from Facebook Pixel and
                                Instagram is also saved and processed by
                                Facebook. Facebook can connect this data with
                                your Facebook or Instagram account and use it
                                for its own and others advertising purposes, in
                                accordance with Facebook's Data Policy which can
                                be found at{" "}
                                <A
                                    href="https://www.facebook.com/about/privacy/"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://www.facebook.com/about/privacy/
                                </A>
                                . Please click{" "}
                                <A
                                    href="https://www.facebook.com/settings/?tab=ads#_=_"
                                    className="text-rose-600 hover:underline"
                                >
                                    here
                                </A>{" "}
                                if you would like to withdraw your consent for
                                use of your data with Facebook Pixel.
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Mixpanel.</P>
                            <P className="text-md text-gray-800">
                                We use Mixpanel, a web analytics service
                                provided by Mixpanel, Inc. ("Mixpanel") to
                                collect certain information relating to your use
                                of our Website. Mixpanel uses "cookies", which
                                are text files placed on your computer, to help
                                our Website analyze how users use the site. This
                                information is used to evaluate your use of the
                                Platform, compile reports on Platform activity,
                                and provide other services related to Platform
                                activity and internet usage. You can find out
                                more about how Mixpanel uses data when you visit
                                our Website by visiting Mixpanel's Privacy
                                Policy at{" "}
                                <A
                                    href="https://mixpanel.com/legal/privacy-policy/"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://mixpanel.com/legal/privacy-policy/
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Fireflies.</P>
                            <P className="text-md text-gray-800">
                                We use Fireflies.ai, a meeting transcription and
                                analysis service provided by Fireflies.ai, Inc.
                                ("Fireflies") to record, transcribe, and analyze
                                audio data from user sessions when permission is
                                granted. Fireflies processes audio recordings
                                and may extract text, metadata, and other
                                information from these recordings. This
                                information is used to provide our services and
                                improve user experience. For more information on
                                how Fireflies handles your data, please visit
                                their Privacy Policy at{" "}
                                <A
                                    href="https://fireflies.ai/privacy-policy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://fireflies.ai/privacy-policy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Typeform.</P>
                            <P className="text-md text-gray-800">
                                We use Typeform, a form-building and survey
                                platform provided by Typeform S.L. ("Typeform")
                                to collect information and feedback from users.
                                When you complete a form or survey created with
                                Typeform, the information you provide is
                                collected and processed by both us and Typeform.
                                This may include your responses, metadata about
                                how you interacted with the form, and technical
                                information such as IP address and browser type.
                                For more information about how Typeform handles
                                your data, please visit their Privacy Policy at{" "}
                                <A
                                    href="https://admin.typeform.com/to/dwk6gt"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://admin.typeform.com/to/dwk6gt
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Anthropic.</P>
                            <P className="text-md text-gray-800">
                                We use Anthropic's AI services to process
                                therapy transcripts, analyze sessions for IFS
                                concepts and create content forms for the
                                report. When you use our Platform, your session
                                transcripts may be processed by Anthropic's AI
                                models to identify patterns, extract insights,
                                and generate content for your reports. For more
                                information about how Anthropic handles your
                                data, please visit their Privacy Policy at{" "}
                                <A
                                    href="https://www.anthropic.com/privacy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://www.anthropic.com/privacy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">OpenAI.</P>
                            <P className="text-md text-gray-800">
                                We use OpenAI's services to process therapy
                                transcripts, analyze sessions for IFS concepts
                                and create content forms for the report. Your
                                session data may be processed by OpenAI's models
                                to generate insights and structured content for
                                your therapy reports. For more information about
                                how OpenAI handles your data, please visit their
                                Privacy Policy at{" "}
                                <A
                                    href="https://openai.com/policies/privacy-policy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://openai.com/policies/privacy-policy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Supabase.</P>
                            <P className="text-md text-gray-800">
                                We use Supabase, a database service provider, to
                                store and manage data on our Platform. Your
                                account information, session data, and other
                                content you create on our Platform may be stored
                                in Supabase's secure databases. For more
                                information about how Supabase handles your
                                data, please visit their Privacy Policy at{" "}
                                <A
                                    href="https://supabase.com/privacy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://supabase.com/privacy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Azure.</P>
                            <P className="text-md text-gray-800">
                                We use Microsoft Azure for cloud hosting
                                services. Your data may be stored and processed
                                on Azure's cloud infrastructure. Microsoft
                                implements comprehensive security measures to
                                protect your data. For more information about
                                how Microsoft Azure handles your data, please
                                visit their Privacy Policy at{" "}
                                <A
                                    href="https://privacy.microsoft.com/en-us/privacystatement"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://privacy.microsoft.com/en-us/privacystatement
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Datadog.</P>
                            <P className="text-md text-gray-800">
                                We use Datadog for database monitoring and
                                performance analytics. Datadog helps us ensure
                                the reliability and security of our database
                                systems. While Datadog may process metadata
                                about database operations, it does not have
                                direct access to your personal information. For
                                more information about how Datadog handles data,
                                please visit their Privacy Policy at{" "}
                                <A
                                    href="https://www.datadoghq.com/legal/privacy/"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://www.datadoghq.com/legal/privacy/
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Google.</P>
                            <P className="text-md text-gray-800">
                                We use Google services including Google
                                Workspace, Gmail, and Google Drive for business
                                operations, email communications, and document
                                storage. When you communicate with us or share
                                documents with us, this information may be
                                processed and stored using Google's services.
                                For more information about how Google handles
                                your data, please visit their Privacy Policy at{" "}
                                <A
                                    href="https://policies.google.com/privacy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://policies.google.com/privacy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">Google Gemini.</P>
                            <P className="text-md text-gray-800">
                                We use Google Gemini, an artificial intelligence
                                platform, to process and analyze data. This may
                                include generating insights from session
                                transcripts and other content you create on our
                                Platform. Google Gemini implements robust
                                security measures to protect your data. For more
                                information about how Google handles your data,
                                please visit their Privacy Policy at{" "}
                                <A
                                    href="https://policies.google.com/privacy"
                                    className="text-rose-600 hover:underline"
                                >
                                    https://policies.google.com/privacy
                                </A>
                                .
                            </P>
                        </LI>
                        <LI>
                            <P className="font-medium">
                                Other third party tools.
                            </P>
                            <P className="text-md text-gray-800">
                                We use other third party tools which allow us to
                                track the performance of our Platform. These
                                tools provide us with information about errors,
                                app and website performance, and other technical
                                details we may use to improve our Platform
                                and/or the Services.
                            </P>
                        </LI>
                    </UL>
                </section>

                <section id="how-do-we-use-your-personal-information">
                    <H3>How do we use your Personal Information?</H3>
                    <P className="text-md mb-2 text-gray-800">
                        We may use your Personal Information for the following
                        purposes:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            Operate, maintain, supervise, administer, and
                            enhance our Platform or the Services, including
                            monitoring and analyzing the effectiveness of
                            content on the Platform, aggregate site usage data,
                            and other usage of the Platform and/or the Services
                            such as assisting you in completing the registration
                            process,
                        </LI>
                        <LI>
                            Process and transcribe audio recordings of therapy
                            sessions,
                        </LI>
                        <LI>Generate analysis and reports,</LI>
                        <LI>
                            Promote and market our Platform and/or the Services
                            to you,
                        </LI>
                        <LI>
                            To provide notices, updates, or important
                            information about your account,
                        </LI>
                        <LI>
                            To fulfill legal or contractual obligations related
                            to, or through enforcement of, any contracts entered
                            into between you and Evolve, including for billing
                            and collection.
                        </LI>
                        <LI>
                            To respond to law enforcement requests, court
                            orders, and subpoenas and to carry out our legal and
                            contractual obligations.
                        </LI>
                        <LI>
                            Authenticate your use of our Platform and Services,
                            detect fraudulent use, and otherwise maintain the
                            security of the Platform,
                        </LI>
                        <LI>
                            Improve our Platform, Services, and AI models when
                            you have explicitly donated your sessions for
                            research and development purposes,
                        </LI>
                        <LI>
                            Any other purpose for which your explicit consent
                            has been obtained.
                        </LI>
                    </UL>
                </section>

                <section id="how-do-we-share-your-personal-information">
                    <H3>How do we share your Personal Information?</H3>
                    <P className="text-md mb-2 text-gray-800">
                        We may share Personal Information with third parties in
                        certain circumstances or for certain purposes,
                        including:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            Our business purposes. We may share your Personal
                            Information with our affiliates, vendors, service
                            providers, and business partners, including our data
                            hosting and data storage partners, analytics and
                            advertising providers, technology services and
                            support, and data security advisors. We may also
                            share your Personal Information with professional
                            advisors, such as auditors, law firms, and
                            accounting firms.
                        </LI>
                        <LI>
                            Service providers. We share recordings and
                            transcripts with our business associates who are
                            necessary to provide Services, including Assembly AI
                            for transcription and OpenAI, Anthropic, and Google
                            for analysis. All such providers are bound by
                            contractual obligations to keep personal information
                            confidential and use it only for the purposes for
                            which we disclose it to them.
                        </LI>
                        <LI>
                            With your consent. We may share your Personal
                            Information if you request or direct us to do so.
                        </LI>
                        <LI>
                            Compliance with law. We may share your Personal
                            Information to comply with applicable law or any
                            obligations thereunder, including cooperation with
                            law enforcement, judicial orders, and regulatory
                            inquiries.
                        </LI>
                        <LI>
                            Business Transfer. We may share your Personal
                            Information to a buyer or other successor in the
                            event of a merger, divestiture, restructuring,
                            reorganization, dissolution, or other sale or
                            transfer of some or all of our assets, whether as a
                            going concern or as part of a bankruptcy,
                            liquidation, or similar proceeding, in which
                            Personal Information held by us about our users are
                            among the assets transferred.
                        </LI>
                        <LI>
                            To enforce our rights. We may share your Personal
                            Information to enforce any applicable{" "}
                            <A
                                href="/terms"
                                className="text-rose-600 hover:underline"
                            >
                                terms and conditions
                            </A>
                            , and to ensure the safety and security of our
                            Services and our users.
                        </LI>
                        <LI>
                            De-identified information. We may also disclose
                            de-identified information, so that it cannot be
                            reasonably used to identify any individual, with
                            third parties for marketing, advertising, research,
                            or similar purposes.
                        </LI>
                        <LI>
                            To improve our Platform. We may use your Personal
                            Information for internal testing, research,
                            analysis, and product development, including to
                            develop and improve our website/application, and to
                            develop, improve, or demonstrate our products and
                            services.
                        </LI>
                        <LI>
                            To market our products and services. We may share
                            your Personal Information with affiliates and third
                            parties to market our products and services.
                        </LI>
                        <LI>
                            To market third party products and services. We may
                            share your Personal Information with affiliates and
                            third parties to market their products or services
                            to you if you have not opted out of these
                            disclosures. For more information on opting out, see
                            Your Choices about how we share your Personal
                            Information.
                        </LI>
                        <LI>
                            Third Party Analytics. We use Mixpanel and other
                            analytics tools to understand and evaluate how
                            visitors interact with our Platform and/or the
                            Services. These tools help us improve our Platform
                            and/or the Services, performance, and your
                            experience.
                        </LI>
                    </UL>
                </section>

                <section id="your-choices-about-how-we-share-your-personal-information">
                    <H3>
                        Your choices about how we share your Personal
                        Information.
                    </H3>
                    <P>
                        This section of our Privacy Policy provides details and
                        explains how to exercise your choices. We offer you
                        choices on how you can opt out of our use of tracking
                        technology, disclosure of your Personal Information for
                        our advertising to you, and other targeted advertising.
                        We do not control the collection and use of your
                        information collected by third parties. These third
                        parties may aggregate the information they collect with
                        information from their other customers for their own
                        purposes. You can opt out of third parties collecting
                        your Personal Information for targeted advertising
                        purposes in the United States by visiting the{" "}
                        <A
                            href="https://optout.networkadvertising.org/?c=1"
                            className="text-rose-600 hover:underline"
                        >
                            National Advertising Initiative's (NAI) opt-out page
                        </A>{" "}
                        and the{" "}
                        <A
                            href="https://optout.aboutads.info/?lang=EN&c=2"
                            className="text-rose-600 hover:underline"
                        >
                            Digital Advertising Alliance's (DAA) opt-out page
                        </A>
                        .
                    </P>

                    <P>
                        Each type of web browser provides ways to restrict and
                        delete cookies. Browser manufacturers provide resources
                        to help you with managing cookies. Please see below for
                        more information.
                    </P>

                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>
                            <A
                                href="https://support.google.com/chrome/answer/95647?hl=en-GB"
                                className="text-rose-600 hover:underline"
                            >
                                Google Chrome
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="https://support.microsoft.com/en-us/kb/260971"
                                className="text-rose-600 hover:underline"
                            >
                                Internet Explorer
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                                className="text-rose-600 hover:underline"
                            >
                                Mozilla Firefox
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="https://support.microsoft.com/en-us/microsoft-edge/view-cookies-in-microsoft-edge-a7d95376-f2cd-8e4a-25dc-1de753474879"
                                className="text-rose-600 hover:underline"
                            >
                                Microsoft Edge
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="https://support.apple.com/en-us/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                                className="text-rose-600 hover:underline"
                            >
                                Safari (Desktop)
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="https://support.apple.com/en-us/HT201265"
                                className="text-rose-600 hover:underline"
                            >
                                Safari (Mobile)
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="http://support.google.com/ics/nexus/bin/answer.py?hl=en&answer=2425067"
                                className="text-rose-600 hover:underline"
                            >
                                Android Browser
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="http://www.opera.com/help"
                                className="text-rose-600 hover:underline"
                            >
                                Opera
                            </A>
                        </LI>
                        <LI>
                            <A
                                href="http://www.opera.com/help/mobile/android#privacy"
                                className="text-rose-600 hover:underline"
                            >
                                Opera Mobile
                            </A>
                        </LI>
                    </UL>

                    <P>
                        For other browsers, please consult the documentation
                        that your browser manufacturer provides. If you do not
                        wish to have your e-mail address used by Evolve Labs to
                        promote our own Products and Services, you can opt-out
                        at any time by clicking the unsubscribe link at the
                        bottom of any e-mail or other marketing communications
                        you receive from us or logging onto your Account
                        Preferences page. This opt out does not apply to
                        information provided to Evolve Labs as a result of a
                        product purchase, or your use of our Platform and/or the
                        Services. You may have other options with respect to
                        marketing and communication preferences through our
                        Platform.
                    </P>

                    <P>
                        You may also see certain ads on other websites because
                        we participate in advertising networks. Ad networks
                        allow us to target our messaging to users through
                        demographic, interest-based, and contextual means. These
                        networks track your online activities over time by
                        collecting information through automated means,
                        including through the use of cookies, web server logs,
                        and web beacons. The networks use this information to
                        show you advertisements that may be tailored to your
                        individual interests.
                    </P>

                    <P>
                        <strong>Session Donation Choices</strong>: You may
                        choose to donate your sessions to {PRODUCT_NAME} for
                        research and development purposes. This can be done at a
                        global level for your entire account or on an individual
                        session basis. When you donate your sessions, our team
                        may access your full transcript to understand feedback
                        and improve our AI models. You can change your donation
                        preferences at any time in your account settings.
                    </P>

                    <P>
                        <strong>GDPR Rights</strong>: Under the General Data
                        Protection Regulation (GDPR), users who are EU residents
                        have certain rights regarding their personal data. You
                        can exercise these rights through your account settings,
                        where you can:
                    </P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>Export your data in a machine-readable format</LI>
                        <LI>Delete your data</LI>
                        <LI>Control your cookie settings</LI>
                        <LI>Object to processing of your personal data</LI>
                        <LI>
                            Request restriction of processing of your personal
                            data
                        </LI>
                    </UL>
                </section>

                <section id="how-do-i-access-and-correct-my-personal-information">
                    <H3>
                        How do I access and correct my Personal Information?
                    </H3>
                    <P>
                        You can review and change your Personal Information by
                        logging into our Services and visiting your account
                        settings. You may also contact us via email at
                        {SUPPORT_EMAIL} to inform us of any changes or errors in
                        any Personal Information we have about you to ensure
                        that it is complete, accurate, and as current as
                        possible or to delete your account. We cannot delete
                        your personal information except by also deleting your
                        account with us. We may also not be able to accommodate
                        your request if we believe it would violate any law or
                        legal requirement or cause the information to be
                        incorrect.
                    </P>
                </section>
            </section>

            <section id="iv-who-may-use-the-services">
                <H2>IV. Who may use the Services?</H2>
                <P>
                    Evolve Labs operates subject to state and federal
                    regulations, and the Platform and/or the Services may not be
                    available in your state. You represent that you are not a
                    person barred from enrolling for or receiving the Services
                    under the laws of the United States or other applicable
                    jurisdictions in which you may be located. Access to and use
                    of the Platform and/or the Services is limited exclusively
                    to users located in states within the United States where
                    the Platform and/or the Services is available. The Platform
                    and/or the Services are not available to users located
                    outside the United States. Accessing the Platform and/or
                    obtaining the Services from jurisdictions where content is
                    illegal, or where we do not offer the Platform and/or the
                    Services, is prohibited.
                </P>
            </section>

            <section id="v-childrens-privacy">
                <H2>V. Children's Privacy</H2>
                <P>
                    We do not knowingly collect or sell Personal Information
                    from children under the age of 18. If you are under the age
                    of 18, do not use or provide any information on or to the
                    Platform or through any of its features. If we learn we have
                    collected or received Personal Information from a child
                    under the age of 18 without verification of parental
                    consent, we will delete it. If you are the parent or
                    guardian of a child under 18 years of age whom you believe
                    might have provided use with their Personal Information, you
                    may Contact Us to request the Personal Information be
                    deleted.
                </P>
            </section>

            <section id="vi-does-evolve-labs-respond-to-do-not-track-signals">
                <H2>VI. Does Evolve Labs respond to Do Not Track signals?</H2>
                <P>
                    Some web browsers have a "Do Not Track" feature. This
                    feature lets you tell websites you visit that you do not
                    want to have your online activity tracked. These features
                    are not yet uniform across browsers. Our Platform is not
                    currently set up to respond to those signals.
                </P>
            </section>

            <section id="vii-data-security">
                <H2>VII. Data Security</H2>
                <P>
                    Evolve Labs has implemented administrative, technical, and
                    physical safeguards designed to protect the data we handle
                    from accidental, intentional, unlawful, or unauthorized
                    access, alteration, destruction, disclosure, or use. These
                    safeguards include, but are limited to:
                </P>
                <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                    <LI>
                        The use of industry standard encryption protocols when
                        recordings and transcripts are at rest and in transit,
                    </LI>
                    <LI>
                        Restricting access to personal information based on job
                        function and justifiable need,
                    </LI>
                    <LI>
                        Maintaining current HIPAA Training Programs for all
                        employees,
                    </LI>
                    <LI>
                        Execution of Business Associate Agreements with all
                        partners who may store or process protected health
                        information for any duration of time,
                    </LI>
                    <LI>
                        Performing regular security, risk, and vulnerability
                        assessments of our infrastructure, supply chain, and
                        processes.
                    </LI>
                </UL>
                <P>
                    We cannot guarantee the security of information transmitted
                    through the Internet. Where you have been given or you have
                    chosen a password, it is your responsibility to keep this
                    password confidential.
                </P>
                <P>
                    The sharing and disclosing of information via the internet
                    is not completely secure. We strive to use best practices
                    and industry standard security measures and tools to protect
                    your data. However, we cannot guarantee the security of
                    Personal Information transmitted to, on, or through our
                    Services. Any transmission of Personal Information is at
                    your own risk. We are not responsible for the circumvention
                    of any privacy settings or security measures contained on
                    our Platform, in your operating system, or mobile device.
                </P>
            </section>

            <section id="viii-californias-shine-the-light-law">
                <H2>VIII. California's Shine the Light Law.</H2>
                <P>
                    California Civil Code Section 1798.83 (California's "Shine
                    the Light" law) permits users of our Platform and/or the
                    Services that are California residents and who provide
                    Personal Information in obtaining products and services for
                    personal, family, or household use to request certain
                    information regarding our disclosure of Personal Information
                    to third parties for their own direct marketing purposes. If
                    applicable, this information would include the categories of
                    Personal Information and the names and addresses of those
                    businesses with which we shared your Personal Information
                    with for the immediately prior calendar year (e.g. requests
                    made in 2021 will receive information regarding such
                    activities in 2020). You may request this information once
                    per calendar year. To make such a request, please Contact Us
                    using the information below.
                </P>
            </section>

            <section id="ix-changes-to-our-privacy-policy">
                <H2>IX. Changes to our Privacy Policy</H2>
                <P>
                    We may update our Privacy Policy periodically to reflect
                    changes in our privacy practices, laws, and best practices.
                    We will post any changes we make to our Privacy Policy on
                    this page with a notice that the Privacy Policy has been
                    updated on our Website's homepage. If we make material
                    changes to our practices with regards to the Personal
                    Information we collect from you, we will notify you by
                    e-mail to the e-mail address specified in your account
                    and/or through a notice on the Platform. The date this
                    Privacy Policy was last revised is identified at the top of
                    the page. You are responsible for ensuring we have an
                    up-to-date active and deliverable e-mail address for you,
                    and for periodically accessing the Platform and reviewing
                    this Privacy Policy to check for any changes.
                </P>
            </section>

            <section id="x-contact-us">
                <H2>X. Contact Us</H2>
                <P>
                    If you have any questions, concerns, complaints or
                    suggestions regarding our Privacy Policy or otherwise need
                    to contact us, you may contact us at{" "}
                    <A
                        href={`mailto:${SUPPORT_EMAIL}`}
                        className="text-rose-600 hover:underline"
                    >
                        {SUPPORT_EMAIL}
                    </A>{" "}
                    or through the "Contact Us" page on the Platform.
                </P>
            </section>
        </LegalPageLayout>
    );
};
